import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Skeleton, Stack, useBreakpointValue } from '@chakra-ui/react';
import type React from 'react';
import { useMemo, useState } from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';
import { FaceliftScrollContainer } from '@/components/Atoms/FaceliftScrollContainer';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import Show from '@/components/Molecules/Show';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import type { DonutChartProps } from './DonutChart.types';

const DonutChart: React.FC<DonutChartProps> = ({
  title = '',
  data: propData,
  isLoading = false,
  compact = false,
}) => {

  const defaultColors = [
    'brand.600', // Amino Tech - matches success color
    'imbedGray', // Imbed - Gray
    'nvisionPurple', // Nvision Biomed - Purple
    'axolotlBlue', // Axolotl Biologix - Blue
    'creamBeige', // Other - Cream/Beige
    'traidPink', // Traid Life Sciences - Pink
  ];

  const chartData = useMemo(() => {
    if (!propData || propData.length === 0) return [];
    const base = compact ? 60 : 75;
    const r0 = compact ? 68 : 85; // highlight radius for first slice
    const r1 = compact ? 64 : 80; // slightly larger radius

    return propData.map((item, index) => ({
      name: item.title,
      value: item.value,
      color: defaultColors[index % defaultColors.length],
      outerRadius: index === 0 ? r0 : index === 3 ? r1 : base,
    }));
  }, [propData, compact]);

  const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());

  const visibleData = useMemo(() => {
    return chartData.filter(item => !disabledSeries.has(item.name));
  }, [chartData, disabledSeries]);

  const chart = useChart({
    data: visibleData,
  });

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const allSeriesDisabled = chart.data.length === 0;

  // Responsive chart positioning: centered on mobile/mid, offset on desktop
  const chartCx = useBreakpointValue({
    base: compact ? '50%' : '30%',  // Mobile - chart more to left, legend on right
    md: '50%',    // Tablet/Mid - centered
    lg: compact ? '50%' : '60%',    // Desktop - offset for legend if not compact
  });

  // Responsive chart width: smaller on mobile/mid to bring legend closer
  const chartWidth = useBreakpointValue({
    base: compact ? 240 : 400,  // Mobile - reduced width
    md: compact ? 240 : 460,    // Tablet/Mid - slightly reduced width
    lg: compact ? 240 : 536,    // Desktop - full width
  });

  // Responsive justification: center on mobile/mid, flex-start on desktop
  const containerJustify = useBreakpointValue({
    base: 'center',      // Mobile - center everything
    md: 'center',        // Tablet/Mid - center everything
    lg: 'flex-start',    // Desktop - align left
  });

  const handleLegendClick = (seriesName: string) => {
    setDisabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };

  // biome-ignore lint/suspicious/noExplicitAny: Recharts label props have dynamic structure
  const renderCustomLabel = (props: Record<string, any>) => {
    const { cx, cy, midAngle, outerRadius, value, name, percent } = props;
    const RADIAN = Math.PI / 180;

    // In compact view, move labels closer to the chart (shorter connector lines)
    let radiusOffset = compact ? 20 : 30;
    if (percent < 0.08) {
      radiusOffset = compact ? 24 : 35;
    } else if (percent < 0.15) {
      radiusOffset = compact ? 22 : 32;
    }

    const radius = outerRadius + radiusOffset;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    const textAnchor = x > cx ? 'start' : 'end';

    return (
      <g>
        <text
          x={x}
          y={y - 7}
          fill={isDark ? colors.lightGray : colors.black}
          textAnchor={textAnchor}
          dominantBaseline="central"
          style={{ fontSize: '12px', fontWeight: 400 }}
        >
          {name.length > 15 ? `${name.substring(0, 12)}...` : name}
        </text>
        <text
          x={x}
          y={y + 6}
          fill={isDark ? colors.lightGray : colors.black}
          textAnchor={textAnchor}
          dominantBaseline="central"
          style={{ fontSize: '12px', fontWeight: 600 }}
        >
          {value.toLocaleString()}
        </text>
      </g>
    );
  };

  // biome-ignore lint/suspicious/noExplicitAny: Recharts tooltip props have dynamic structure
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;

      return (
        <Box
          bg={isDark ? colors.darkModeBgColor : 'white'}
          border="1px solid"
          borderColor={isDark ? '#3a3a3a' : 'gray.200'}
          borderRadius="6px"
          px={3}
          py={2}
          boxShadow="lg"
        >
          <FaceliftText
            fontSize="sm"
            fontWeight="600"
            mb={1.5}
            _dark={{ color: 'white' }}
            _light={{ color: 'black' }}
          >
            {data.name}
          </FaceliftText>
          <FaceliftText
            fontSize="xs"
            mb={0.5}
            _dark={{ color: 'gray.300' }}
            _light={{ color: 'gray.700' }}
          >
            Value: {data.value.toLocaleString()}
          </FaceliftText>
          <FaceliftText
            fontSize="xs"
            _dark={{ color: 'gray.300' }}
            _light={{ color: 'gray.700' }}
          >
            Percentage: {percentage}%
          </FaceliftText>
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      <Show when={isLoading}>
        <Box
          width="100%"
          height={compact ? '264px' : '310px'}
          borderRadius="5px"
          borderWidth="1px"
          borderColor="gray.400"
          p={3}
          pl={2}
          _dark={{ bg: 'darkModeBgColor' }}
          _light={{ bg: 'white' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Skeleton height="20px" width="60%" maxW="240px" mb={2} ml={compact ? 0 : 4} mx={compact ? 'auto' : 0} _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
          <Box flex="1" px={2} py={2} display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={4}>
            <Box
              position="relative"
              width={{ base: '160px', sm: '180px', md: '200px' }}
              height={{ base: '160px', sm: '180px', md: '200px' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              {/* Outer circle */}
              <Skeleton height="100%" width="100%" borderRadius="full" position="absolute" _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
              {/* Inner circle to create donut effect */}
              <Box
                height={{ base: '64px', sm: '72px', md: '80px' }}
                width={{ base: '64px', sm: '72px', md: '80px' }}
                borderRadius="full"
                position="absolute"
                _dark={{ bg: 'darkModeBgColor' }}
                _light={{ bg: 'white' }}
                zIndex={1}
              />
            </Box>
            <Stack direction="row" justify="center" gap={3} flexWrap="wrap">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton
                  // biome-ignore lint/suspicious/noArrayIndexKey: Temporary skeleton loader
                  key={i}
                  height="16px"
                  width="100px"
                  _dark={{ bg: 'gray.700' }}
                  _light={{ bg: 'gray.200' }}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </Show>

      <Show when={!isLoading}>
        <Box
          width="100%"
          height={compact ? '264px' : '310px'}
          borderRadius="5px"
          borderWidth="1px"
          borderColor="gray.400"
          p={3}
          _dark={{ bg: 'darkModeBgColor' }}
          _light={{ bg: 'white' }}
          overflow="hidden"
          display="flex"
          flexDirection="column"
          alignItems={compact ? 'center' : 'stretch'}
        >
          <Show when={!!title}>
            <FaceliftText
              fontSize="md"
              fontWeight="semibold"
              mb={2}
              mt={1}
              ml={compact ? 0 : 4}
              w="100%"
              textAlign={compact ? 'center' : 'left'}
              _dark={{ color: 'white' }}
              _light={{ color: 'black' }}
            >
              {title}
            </FaceliftText>
          </Show>

          <Show when={compact}>
            <FaceliftScrollContainer
              width="100%"
              height="210px"
              minWidth="308px"
              showScrollbarY={false}
            >
              <Box
                w="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap={{ base: 2, md: 1, lg: 0 }}
                position="relative"
                minH="200px"
                mb={2}
              >
                {/* Compact view - chart and legend side by side */}
                <Show when={allSeriesDisabled}>
                  <Box flex="1" display="flex" alignItems="center" justifyContent="center">
                    <FaceliftText fontSize="md" fontWeight="semibold" _dark={{ color: 'gray.400' }} _light={{ color: 'gray.500' }}>
                      No data found
                    </FaceliftText>
                  </Box>
                </Show>

                <Show when={!allSeriesDisabled}>
                  <Box
                    width="300px"
                    minW="280px"
                    height="191px"
                    flexShrink={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Box width="100%" height="100%">
                      <Chart.Root height="100%" width="100%" chart={chart}>
                        <PieChart width={chartWidth} height={190}>
                          <Pie
                            data={chart.data}
                            cx="50%"
                            cy="50%"
                            startAngle={120}
                            endAngle={480}
                            labelLine={{
                              stroke: isDark ? colors.lightGray : colors.ironGray,
                              strokeWidth: 0.8,
                            }}
                            label={renderCustomLabel}
                            outerRadius={
                              (entry: { outerRadius?: number }) => entry.outerRadius || 75
                            }
                            innerRadius={18}
                            dataKey={chart.key('value')}
                            nameKey={chart.key('name')}
                            isAnimationActive={false}
                            cornerRadius={4}
                            paddingAngle={0.5}
                            stroke={isDark ? 'white' : 'white'}
                            strokeWidth={2}
                            strokeLinejoin="round"
                          >
                            {chart.data.map((item) => (
                              <Cell key={item.name} fill={chart.color(item.color)} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </Chart.Root>
                    </Box>
                  </Box>
                </Show>

                {/* Legend for compact view */}
                <Box
                  width="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                  flexShrink={0}
                >
                  <Stack
                    direction="column"
                    justify="center"
                    align="flex-start"
                    gap={2}
                  >
                    {chartData.map((item) => {
                      const isDisabled = disabledSeries.has(item.name);
                      return (
                        <Stack
                          key={item.name}
                          direction="row"
                          align="center"
                          gap={1.5}
                          cursor="pointer"
                          onClick={() => handleLegendClick(item.name)}
                          opacity={isDisabled ? 0.4 : 1}
                          transition="opacity 0.2s"
                          _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
                        >
                          <Box width={2.5} height={2.5} borderRadius="full" bg={item.color} />
                          <FaceliftText
                            fontSize="2xs"
                            fontWeight="medium"
                            lineHeight="tight"
                            _dark={{ color: 'gray.200' }}
                            _light={{ color: 'black' }}
                            whiteSpace="nowrap"
                            textDecoration={isDisabled ? 'line-through' : 'none'}
                          >
                            {item.name}
                          </FaceliftText>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            </FaceliftScrollContainer>
          </Show>

          <Show when={!compact}>
            <Box
              w="100%"
              overflowX="auto"
              display="flex"
              justifyContent={containerJustify}
              alignItems="center"
              position="relative"
            >
              {/* Non-compact view - original layout */}
              <Show when={allSeriesDisabled}>
                <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={10}>
                  <FaceliftText fontSize="md" fontWeight="semibold" _dark={{ color: 'gray.400' }} _light={{ color: 'gray.500' }}>
                    No data found
                  </FaceliftText>
                </Box>
              </Show>

              <FaceliftScrollContainer
                height="100%"
                minWidth="666px"
                pt={6}
                pb={3}
              >
                <Box position="relative" height="calc(100% - 28px)" width="100%" display="flex" flexDirection="row" justifyContent={containerJustify} mb={2}>
                  <Box width={chartWidth} height="100%">
                    <Show when={!allSeriesDisabled}>
                      <Chart.Root height="100%" width="100%" chart={chart}>
                        <PieChart width={chartWidth} height={240}>
                          <Pie
                            data={chart.data}
                            cx={chartCx}
                            cy="55%"
                            startAngle={120}
                            endAngle={480}
                            labelLine={{
                              stroke: isDark ? colors.lightGray : colors.ironGray,
                              strokeWidth: 1,
                            }}
                            label={renderCustomLabel}
                            outerRadius={
                              (entry: { outerRadius?: number }) => entry.outerRadius || 75
                            }
                            innerRadius={18}
                            dataKey={chart.key('value')}
                            nameKey={chart.key('name')}
                            isAnimationActive={false}
                            cornerRadius={4}
                            paddingAngle={0.5}
                            stroke={isDark ? 'white' : 'white'}
                            strokeWidth={2}
                            strokeLinejoin="round"
                          >
                            {chart.data.map((item) => (
                              <Cell key={item.name} fill={chart.color(item.color)} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </Chart.Root>
                    </Show>
                  </Box>
                  <Box width="120px" display="flex" alignItems="center" justifyContent="flex-start">
                    <Stack direction="column" justify="center" align="flex-start" gap={2}>
                      {chartData.map((item) => {
                        const isDisabled = disabledSeries.has(item.name);
                        return (
                          <Stack
                            key={item.name}
                            direction="row"
                            align="center"
                            gap={1.5}
                            cursor="pointer"
                            onClick={() => handleLegendClick(item.name)}
                            opacity={isDisabled ? 0.4 : 1}
                            transition="opacity 0.2s"
                            _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
                          >
                            <Box width={2.5} height={2.5} borderRadius="full" bg={item.color} />
                            <FaceliftText
                              fontSize="2xs"
                              fontWeight="medium"
                              lineHeight="tight"
                              _dark={{ color: 'gray.200' }}
                              _light={{ color: 'black' }}
                              whiteSpace="nowrap"
                              textDecoration={isDisabled ? 'line-through' : 'none'}
                            >
                              {item.name}
                            </FaceliftText>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              </FaceliftScrollContainer>
            </Box>
          </Show>
        </Box>
      </Show>
    </>
  );
};

export default DonutChart;

